<template>
  <v-container grid-list-lg fluid class="setting-page pa-4">
    <v-card :loading="loading" outlined class="setting-card">
      <div class="setting-header">
        <div class="setting-title-wrap">
          <v-btn icon small class="mr-2" @click="$router.go(-1)">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <div>
            <div class="page-title">Setting</div>
            <div class="page-subtitle">
              Manage business info, invoice, inventory, and remote ordering
            </div>
          </div>
        </div>

        <v-chip v-if="Setting" small label class="font-weight-bold">
          Code: {{ Setting.code }}
        </v-chip>
      </div>

      <div class="section-block">
        <div class="section-title">Business Info</div>
        <div class="section-subtitle">Basic business profile and address</div>
      </div>
      <div v-if="Setting">
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="12">
              <v-text-field
                outlined
                dense
                :error-messages="nameErrors"
                :hide-details="nameErrors.length === 0"
                v-model="Setting.name"
                label="Business Name"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-text-field
                outlined
                dense
                :error-messages="emailErrors"
                :hide-details="emailErrors.length === 0"
                v-model="Setting.email"
                label="Email Address"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-text-field
                outlined
                dense
                :error-messages="phoneNumberErrors"
                :hide-details="phoneNumberErrors.length === 0"
                v-model="Setting.phoneNumber"
                label="Phone Number"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12">
              <v-text-field
                outlined
                hide-details
                dense
                v-model="Setting.website"
                label="Web Site"
              ></v-text-field>
            </v-col>
            <v-card-title primary-title> Address </v-card-title>
            <v-col cols="12" sm="12">
              <GoogleAddressInput
                label="Search Address"
                @address-selected="setAddress"
              />
            </v-col>
            <v-col cols="12" sm="12">
              <v-text-field
                outlined
                hide-details
                dense
                v-model="Setting.address"
                label="Address"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-text-field
                outlined
                dense
                hide-details
                v-model="Setting.city"
                label="City"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="3">
              <v-text-field
                outlined
                dense
                hide-details
                v-model="Setting.state"
                label="State"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="3">
              <v-text-field
                outlined
                hide-details
                dense
                v-model="Setting.zip"
                label="Zip Code"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12">
              <v-textarea
                auto-grow
                hide-details
                outlined
                dense
                v-model="Setting.policy"
                label="Policy"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>

        <div class="section-block marketplace-heading">
          <div>
            <div class="section-title">Integrations</div>
            <div class="section-subtitle">
              Choose where your wholesale catalog can receive orders
            </div>
          </div>
        </div>

        <v-card-text class="marketplace-section">
          <v-card outlined class="marketplace-card">
            <v-card-text>
              <div class="marketplace-card__content">
                <div class="marketplace-card__icon" aria-hidden="true">
                  <v-icon color="secondary">mdi-storefront-outline</v-icon>
                </div>

                <div class="marketplace-card__copy">
                  <div class="marketplace-card__heading">
                    <div>
                      <div class="subtitle-1 font-weight-bold">
                        Market
                      </div>
                      <v-chip
                        small
                        label
                        :color="marketplaceStatusColor"
                        :outlined="!repairNestMarketplaceEnabled"
                        class="mt-2"
                      >
                        {{
                          repairNestMarketplaceLoading
                            ? "Loading"
                            : repairNestMarketplaceEnabled
                            ? marketplaceStatusLabel
                            : "Not requested"
                        }}
                      </v-chip>
                    </div>

                    <v-switch
                      v-model="repairNestMarketplaceEnabled"
                      inset
                      :loading="repairNestMarketplaceSaving"
                      :disabled="
                        repairNestMarketplaceLoading ||
                        repairNestMarketplaceSaving
                      "
                      :aria-label="`${
                        repairNestMarketplaceEnabled ? 'Withdraw from' : 'Request access to'
                      } Market`"
                      @change="updateRepairNestMarketplace"
                    />
                  </div>

                  <p class="marketplace-card__description mb-2">
                    Request to publish your wholesale catalog in participating
                    POS brands. After approval, stores using those brands can
                    find your active catalog-order products and send orders
                    directly into SMPL Sale.
                  </p>
                  <p class="marketplace-card__description mb-0">
                    You continue handling payment directly with the customer.
                    This integration does not change your existing products,
                    orders, or daily workflow.
                  </p>

                  <v-alert
                    v-if="repairNestMarketplaceEnabled"
                    :type="repairNestMarketplaceApprovalStatus === 'approved' ? 'success' : 'info'"
                    outlined
                    dense
                    class="mt-4 mb-0"
                  >
                    <template v-if="repairNestMarketplaceApprovalStatus === 'approved'">
                      Approved for: {{ approvedBrandNames }}
                    </template>
                    <template v-else>
                      Your request is waiting for POS administrator approval.
                    </template>
                  </v-alert>

                  <v-alert
                    v-if="repairNestMarketplaceError"
                    type="error"
                    outlined
                    dense
                    class="mt-4 mb-0"
                  >
                    {{ repairNestMarketplaceError }}
                    <v-btn
                      text
                      small
                      color="error"
                      :loading="repairNestMarketplaceLoading"
                      @click="loadRepairNestMarketplace"
                    >
                      Retry
                    </v-btn>
                  </v-alert>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>

        <div class="section-block">
          <div class="section-title">Options</div>
          <div class="section-subtitle">
            System behavior and module settings
          </div>
        </div>
        <v-expansion-panels class="setting-panels">
          <v-expansion-panel>
            <v-expansion-panel-header> Product </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Product Cost</div>
                  <div>
                    Choose which cost display in Product list average cost or
                    real cost
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.showAvgCostOnProduct"
                    inset
                    :label="
                      Setting.metadata.showAvgCostOnProduct
                        ? 'Avg Cost'
                        : 'Cost'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Dynamic Price</div>
                  <div>Price will change base on Dynamic Price List</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.dynamicPrice"
                    inset
                    @change="changeDynamicPrice"
                    :label="Setting.metadata.dynamicPrice ? 'On' : 'Off'"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Delete</div>
                  <div>
                    Extra precaution ask to type <span>delete</span> before
                    deleting any item or product
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.deletePrecaution"
                    inset
                    :label="
                      Setting.metadata.deletePrecaution ? 'Enable' : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Out of Stock</div>
                  <div>Show out of stock label in product list.</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.outOfStock"
                    inset
                    :label="Setting.metadata.outOfStock ? 'Show' : 'Hide'"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <!-- <div class="subtitle-2">Out of Stock</div> -->
                  <div>Don't allow out of stock items to be sale.</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.lockOutOfStock"
                    inset
                    :label="
                      Setting.metadata.lockOutOfStock ? 'Enable' : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Product description</div>
                  <div>Show or hide description on product list.</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.productDescription"
                    inset
                    :label="
                      Setting.metadata.productDescription ? 'Show' : 'Hide'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Box Count</div>
                  <div>
                    This feature allows you to enable box count on invoices and
                    purchases.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.boxCountOnInventory"
                    inset
                    :label="
                      Setting.metadata.boxCountOnInventory
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Parent Chile Product</div>
                  <div>
                    This feature allows you to create parent and child products
                    under a parent product for reference.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.parentChile"
                    inset
                    :label="Setting.metadata.parentChile ? 'Enable' : 'Disable'"
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Pricing Model</div>
                  <div>
                    Enable single customer pricing instead of using a custom
                    tier system.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.isSingleCustomerPricing"
                    inset
                    :label="
                      Setting.metadata.isSingleCustomerPricing
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <!-- <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Price Tire System</div>
                  <div>
                    Price Tier System lets you apply positive or negative
                    discount values to the base product price, so you can create
                    custom pricing for different customer types like retail,
                    wholesale, or VIP.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-btn color="primary" dark>Price Tire</v-btn>
                </v-col>
              </v-row> -->
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header> Invoice </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Open Invoice</div>
                  <div>Show open invoice list on every invoice.</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.openInvoiceListOnInvoice"
                    inset
                    :label="
                      Setting.metadata.openInvoiceListOnInvoice
                        ? 'Show'
                        : 'Hide'
                    "
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0" cols="8">
                  <div class="subtitle-2">Invoice Number</div>
                  <div>Enter invoice starting invoice number.</div>
                </v-col>
                <v-col class="text-right py-0" cols="4">
                  <v-text-field
                    v-model="Setting.startingInvoiceNumber"
                    hide-details
                    outlined
                    @focus="$event.target.select()"
                    dense
                    label="Invoice #"
                    ref="invoiceNumber"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0" cols="8">
                  <div class="subtitle-2">Public Viewing Invoice</div>
                  <div>Limit on public viewing invoice.</div>
                </v-col>
                <v-col class="text-right py-0" cols="4">
                  <DateSelector
                    v-model="Setting.metadata.invoiceLimit"
                    :initValue="Setting.metadata.invoiceLimit"
                    :emptyDate="Setting.metadata.invoiceLimit ? false : true"
                  />
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0" cols="8">
                  <div class="subtitle-2">Terms</div>
                  <div>Limit invoice due date days.</div>
                </v-col>
                <v-col class="text-right py-0" cols="4">
                  <DayPicker
                    v-model="Setting.metadata.invoiceDue"
                    :initValue="Setting.metadata.invoiceDue"
                    @input="setDue"
                  />
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0" cols="8">
                  <div class="subtitle-2">Invoice Viewing Limit</div>
                  <div>Set invoice default viewing limit.</div>
                </v-col>
                <v-col class="text-right py-0" cols="4">
                  <DateTimePicker
                    :defaultRange="
                      Setting.metadata.invoiceViewLimit
                        ? Setting.metadata.invoiceViewLimit.range
                        : null
                    "
                    :range="true"
                    :hideTime="true"
                    v-model="Setting.metadata.invoiceViewLimit"
                    :hideCustomOption="true"
                  />
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Invoice Due Date</div>
                  <div>Show due date on invoice list</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.showDueDateOnInvoice"
                    inset
                    :label="
                      Setting.metadata.showDueDateOnInvoice ? 'Show' : 'Hide'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Lock Sales Rep</div>
                  <div>
                    If this feature is enabled, a Sales Rep must be selected
                    when creating or updating a invoice
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.lockInvoiceSalesRep"
                    inset
                    :label="
                      Setting.metadata.lockInvoiceSalesRep
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Multi-line Item Name</div>
                  <div>
                    Turn this on to add multiple item rows by name while making
                    or editing an invoice.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.multiLineItemName"
                    inset
                    :label="
                      Setting.metadata.multiLineItemName ? 'Enable' : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header> Invoice Printing </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row align="center">
                <v-col cols="12" class="pb-0">
                  <div>
                    Control how invoices and packing slips open, and choose
                    which pricing details appear on printed documents.
                  </div>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Print Serial Number</div>
                  <div>Show product serial or IMEI numbers on the invoice.</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.printSerialNumberOnInvoice"
                    inset
                    :label="Setting.metadata.printSerialNumberOnInvoice ? 'Show' : 'Hide'"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0" cols="8">
                  <div class="subtitle-2">Invoice Format</div>
                  <div>
                    Choose whether invoice previews use the browser-friendly
                    HTML layout or the downloadable PDF layout.
                  </div>
                </v-col>
                <v-col class="text-right py-0" cols="4">
                  <v-btn-toggle
                    v-model="Setting.metadata.invoicePreviewFormat"
                    mandatory
                    color="primary"
                    class="invoice-preview-toggle"
                  >
                    <v-btn value="html">HTML</v-btn>
                    <v-btn value="pdf">PDF</v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0" cols="8">
                  <div class="subtitle-2">Packing Slip Format</div>
                  <div>
                    Choose the HTML or PDF layout independently for packing
                    slips.
                  </div>
                </v-col>
                <v-col class="text-right py-0" cols="4">
                  <v-btn-toggle
                    v-model="Setting.metadata.packingSlipPreviewFormat"
                    mandatory
                    color="primary"
                    class="invoice-preview-toggle"
                  >
                    <v-btn value="html">HTML</v-btn>
                    <v-btn value="pdf">PDF</v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Show Unit Price</div>
                  <div>
                    Add a U/PRICE column calculated from the box price and the
                    product's units-per-box quantity. PRICE always shows the
                    full box price.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.showUnitPriceOnInvoice"
                    inset
                    :label="Setting.metadata.showUnitPriceOnInvoice ? 'Show' : 'Hide'"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Show Line Number</div>
                  <div>
                    Add a numbered line column to invoice and packing-slip
                    item tables.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.showLineNumberOnInvoice"
                    inset
                    :label="Setting.metadata.showLineNumberOnInvoice ? 'Show' : 'Hide'"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Print Invoice Number Barcode</div>
                  <div>
                    Show the invoice number as a barcode on invoices and
                    packing slips in both HTML and PDF formats.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.barcodeOnInvoice"
                    inset
                    :label="Setting.metadata.barcodeOnInvoice ? 'Show' : 'Hide'"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Hide Balance on Invoice</div>
                  <div>
                    Enable this option to hide the remaining balance on printed
                    or viewed customer invoices.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.hideCustomerBalanceOnInvoice"
                    inset
                    :label="
                      Setting.metadata.hideCustomerBalanceOnInvoice
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0" cols="8">
                  <div class="subtitle-2">Printed Invoice Label</div>
                  <div>
                    Choose the label shown at the top of printed invoice
                    layouts.
                  </div>
                </v-col>
                <v-col class="text-right py-0" cols="4">
                  <v-select
                    v-model="Setting.metadata.invoicePrintLabel"
                    :items="invoicePrintLabelItems"
                    item-text="text"
                    item-value="value"
                    hide-details
                    outlined
                    dense
                    label="Invoice label"
                  ></v-select>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Make Payment QR Code</div>
                  <div>
                    Print a prominent scan-to-pay QR code on every invoice.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.paymentQrCodeEnabled"
                    inset
                    :label="Setting.metadata.paymentQrCodeEnabled ? 'Enable' : 'Disable'"
                  />
                </v-col>
              </v-row>

              <v-row
                v-if="Setting.metadata.paymentQrCodeEnabled"
                align="center"
              >
                <v-col class="py-0" cols="8">
                  <div class="subtitle-2">Payment Link</div>
                  <div>Enter the web address opened when the QR code is scanned.</div>
                </v-col>
                <v-col class="text-right py-0" cols="4">
                  <v-text-field
                    v-model.trim="Setting.metadata.paymentLink"
                    label="Payment link"
                    placeholder="https://payments.example.com/your-business"
                    hint="Customers will be sent to this address after scanning the invoice."
                    persistent-hint
                    outlined
                    dense
                    type="url"
                  />
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Payment Date</div>
                  <div>Show or hide payment dates on printed invoices.</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.hidePaymentDate"
                    inset
                    :label="Setting.metadata.hidePaymentDate ? 'Show' : 'Hide'"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Payment Note</div>
                  <div>Show payment notes on the invoice.</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.showPaymentNoteOnInvoice"
                    inset
                    :label="Setting.metadata.showPaymentNoteOnInvoice ? 'Show' : 'Hide'"
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Order Again QR Code</div>
                  <div>Show or hide the reorder QR code on HTML invoices.</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.QRCodeOnInvoice"
                    inset
                    :label="Setting.metadata.QRCodeOnInvoice ? 'Hide' : 'Show'"
                  ></v-switch>
                </v-col>
              </v-row>

            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header> Notification </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Automatic Invoice Due Reminders</div>
                  <div>
                    Email customers at 9:00 AM one week before the due date,
                    on the due date, and weekly while the invoice is past due.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.invoiceDueEmailReminders"
                    inset
                    :label="Setting.metadata.invoiceDueEmailReminders ? 'On' : 'Off'"
                  />
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">
                    Send Text Message on Invoice Delete
                  </div>

                  <div>
                    Send a text alert to the selected phone numbers when an
                    invoice is voided or deleted.
                  </div>
                </v-col>

                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.sendSMSOnInvoiceDelete"
                    inset
                    :label="
                      Setting.metadata.sendSMSOnInvoiceDelete
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>

              <div
                v-if="Setting.metadata.sendSMSOnInvoiceDelete"
                class="notification-phone-section"
              >
                <v-alert
                  dense
                  text
                  outlined
                  type="warning"
                  icon="mdi-currency-usd"
                  class="mb-4"
                >
                  This notification feature costs $20 per month.
                </v-alert>

                <div class="notification-phone-header">
                  <div>
                    <div class="subtitle-2">Alert Phone Numbers</div>
                    <div>
                      Add the U.S. phone numbers that should receive invoice
                      deletion alerts.
                    </div>
                  </div>

                  <v-chip
                    small
                    label
                    color="primary"
                    outlined
                    class="font-weight-bold"
                  >
                    {{ invoiceDeletePhoneNumbers.length }}
                    {{
                      invoiceDeletePhoneNumbers.length === 1
                        ? "number"
                        : "numbers"
                    }}
                  </v-chip>
                </div>

                <v-row align="start" class="phone-input-row">
                  <v-col cols="12" sm="8" md="6">
                    <v-text-field
                      v-model="newInvoiceDeletePhone"
                      outlined
                      dense
                      maxlength="14"
                      label="U.S. Phone Number"
                      placeholder="(347) 555-1234"
                      prepend-inner-icon="mdi-phone-outline"
                      :error-messages="invoiceDeletePhoneError"
                      :hide-details="!invoiceDeletePhoneError.length"
                      @input="formatInvoiceDeletePhoneInput"
                      @keyup.enter="addInvoiceDeletePhone"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="4" md="3">
                    <v-btn
                      block
                      rounded
                      color="primary"
                      dark
                      :disabled="!canAddInvoiceDeletePhone"
                      @click="addInvoiceDeletePhone"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      Add Number
                    </v-btn>
                  </v-col>
                </v-row>

                <div class="phone-help-text">
                  Enter a valid 10-digit U.S. phone number. The country code +1
                  is optional.
                </div>

                <div
                  v-if="invoiceDeletePhoneNumbers.length"
                  class="notification-phone-list"
                >
                  <div
                    v-for="(phone, index) in invoiceDeletePhoneNumbers"
                    :key="`${phone}-${index}`"
                    class="notification-phone-item"
                  >
                    <div class="notification-phone-info">
                      <div class="notification-phone-icon">
                        <v-icon small color="primary"
                          >mdi-cellphone-message</v-icon
                        >
                      </div>

                      <div>
                        <div class="notification-phone-number">
                          {{ formatPhoneNumber(phone) }}
                        </div>

                        <div class="notification-phone-label">
                          Invoice deletion alerts
                        </div>
                      </div>
                    </div>

                    <v-btn
                      icon
                      small
                      color="red"
                      title="Remove phone number"
                      @click="removeInvoiceDeletePhone(index)"
                    >
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </div>

                <div v-else class="notification-empty-state">
                  <v-icon color="grey lighten-1" class="mb-2">
                    mdi-phone-plus-outline
                  </v-icon>

                  <div class="font-weight-bold">No phone numbers added</div>

                  <div class="caption grey--text">
                    Add at least one phone number to receive alerts.
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header> Inventory </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Forced inventory tracking</div>
                  <div>
                    Inventory tracking will automatically be turned on by
                    default whenever you create a new product.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.ForcedInventoryTracking"
                    inset
                    :label="
                      Setting.metadata.ForcedInventoryTracking
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Purchase</div>
                  <div>
                    This feature lets you update the selling price at the time
                    of purchase. Once you add an item to the basket, it will
                    update the selling price of the selected product.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.updateProductPriceOnPurchase"
                    inset
                    :label="
                      Setting.metadata.updateProductPriceOnPurchase
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Lock Product Name</div>
                  <div>
                    Don't allow to change product name on invoices and purchases
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.lockProductName"
                    inset
                    :label="
                      Setting.metadata.lockProductName ? 'Enable' : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">UPC Code</div>
                  <div>
                    You can choose how you want the UPC to behave in your
                    system. With Direct Insert, the product will automatically
                    drop into the shopping cart after scanning or entering the
                    UPC, making it ideal for quick, single-item transactions.
                    Alternatively, with Stay on Search Bar, the product will
                    remain in the search bar after scanning, allowing you to
                    manually enter the quantity before adding it to the cart.
                    This option is helpful when you need to add multiple
                    quantities of the same item.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.UPCbehave"
                    inset
                    :label="Setting.metadata.UPCbehave ? 'Stay' : 'Insert'"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Lock Reference Number</div>
                  <div>
                    If this feature is enabled, a reference number must be
                    entered when creating or updating a purchase
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.lockPurchaseReferenceNumber"
                    inset
                    :label="
                      Setting.metadata.lockPurchaseReferenceNumber
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">
                    Status Update for Serialized Products
                  </div>
                  <div>
                    If this feature is enabled, users can change the status of
                    serialized products while creating or updating a purchase.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.updateSerializeProductStatus"
                    inset
                    :label="
                      Setting.metadata.updateSerializeProductStatus
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header> CRM </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Duplicate Customer Name</div>
                  <div>Don't allow to create customer with same name</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.restrictDuplicateCustomerName"
                    inset
                    :label="
                      Setting.metadata.restrictDuplicateCustomerName
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel
            v-if="Setting.metadata && Setting.metadata.dynamicPrice"
          >
            <v-expansion-panel-header> Dynamic Price </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-data-table
                :mobile-breakpoint="0"
                :headers="headers"
                :items="Setting.metadata.dynamicPriceList"
                hide-default-footer
                :items-per-page="Setting.metadata.dynamicPriceList.length"
                sort-by="from"
                class="elevation-0"
              >
                <template v-slot:top>
                  <v-toolbar flat>
                    <v-toolbar-title>Price List</v-toolbar-title>
                    <v-divider class="mx-4" inset vertical></v-divider>
                    <v-spacer></v-spacer>
                    <v-dialog v-model="dialog" max-width="500px">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          color="primary"
                          dark
                          class="mb-2"
                          v-bind="attrs"
                          v-on="on"
                        >
                          New Item
                        </v-btn>
                      </template>
                      <v-card>
                        <v-card-title>
                          <span class="text-h5">{{ formTitle }}</span>
                        </v-card-title>

                        <v-card-text>
                          <v-container>
                            <v-row>
                              <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                  autofocus
                                  v-model="editedItem.from"
                                  label="From"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                  v-model="editedItem.to"
                                  label="To"
                                ></v-text-field>
                              </v-col>
                              <v-col cols="12" sm="6" md="4">
                                <v-text-field
                                  v-model="editedItem.markup"
                                  label="Mark Up"
                                ></v-text-field>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-card-text>

                        <v-card-actions>
                          <v-spacer></v-spacer>
                          <v-btn color="primary" text @click="close">
                            Cancel
                          </v-btn>
                          <v-btn color="primary" text @click="save">
                            Save
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                    <v-dialog v-model="dialogDelete" max-width="500px">
                      <v-card>
                        <v-card-title class="text-h5"
                          >Are you sure you want to delete this
                          item?</v-card-title
                        >
                        <v-card-actions>
                          <v-spacer></v-spacer>
                          <v-btn color="primary" text @click="closeDelete"
                            >Cancel</v-btn
                          >
                          <v-btn color="primary" text @click="deleteItemConfirm"
                            >OK</v-btn
                          >
                          <v-spacer></v-spacer>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                  </v-toolbar>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-icon small class="mr-2" @click="editItem(item)">
                    mdi-pencil
                  </v-icon>
                  <v-icon small @click="deleteItem(item)"> mdi-delete </v-icon>
                </template>
                <template v-slot:no-data>
                  <v-btn color="primary" @click="initialize"> Reset </v-btn>
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header>
              Remote Ordering
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <!-- <v-row>
                <v-col cols="12">
                  <v-text-field outlined dense hide-details v-model="Setting.metadata.dynamicSerializeLabel" label="Serialize"></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field outlined dense hide-details v-model="Setting.metadata.dynamicNonSerializeLabel" label="Non Serialize"></v-text-field>
                </v-col>
              </v-row> -->

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Business</div>
                  <div>View or Hide Name of your business</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.remoteOrderBusinessName"
                    inset
                    :label="
                      Setting.metadata.remoteOrderBusinessName ? 'View' : 'Hide'
                    "
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Collections</div>
                  <div>
                    Enable this feature to make customers pick a collection
                    before they start ordering. Only the categories under that
                    chosen collection will show.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    :disabled="!Setting.metadata.collections"
                    v-model="Setting.metadata.forceCollection"
                    inset
                    :label="
                      Setting.metadata.forceCollection ? 'Enable' : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Category</div>
                  <div>
                    View or Hide Categories While Ordering on top of the list
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.remoteOrderCategory"
                    inset
                    :label="
                      Setting.metadata.remoteOrderCategory ? 'View' : 'Hide'
                    "
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Arrange Category</div>
                  <div>
                    Set the order customers see on the remote order link.
                  </div>
                </v-col>

                <v-col class="text-right shrink py-0">
                  <ArrangeCategoryOrder />
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Stock</div>
                  <div>
                    Enable this feature if you want to hide the stock quantity
                    on remote orders.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.remoteOrderStockCount"
                    inset
                    :label="
                      Setting.metadata.remoteOrderStockCount
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Out of stock</div>
                  <div>Enable this feature to show item that out of stock.</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.showOutOfStockItems"
                    inset
                    :label="
                      Setting.metadata.showOutOfStockItems
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Out of stock item order</div>
                  <div>
                    Enable this feature to let customers place orders for items
                    even if they’re out of stock.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.orderOutStockItem"
                    inset
                    :label="
                      Setting.metadata.orderOutStockItem ? 'Enable' : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Color</div>
                  <div>
                    Make it your own change background color of you order
                    screen.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-color-picker
                    dot-size="30"
                    mode="hexa"
                    v-model="color"
                  ></v-color-picker>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col class="py-0">
                  <!-- <div class="subtitle-2">Color</div> -->
                  <div>
                    Dark Mode
                    <v-btn
                      :dark="Setting.metadata.remoteOrderDarkMode"
                      class="ml-2"
                      :color="showColor"
                      >Test Button</v-btn
                    >
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.remoteOrderDarkMode"
                    inset
                    :label="
                      Setting.metadata.remoteOrderDarkMode
                        ? 'Enable'
                        : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Image on orde</div>
                  <div>Hide image on sales order to customers</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.viewImageOnOrder"
                    inset
                    :label="Setting.metadata.hideImageOnOrder ? 'Hide' : 'View'"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Special Product Tab</div>
                  <div>
                    Hide Special Product Tab on the remote ordering screen.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.hideSpecialProductTabOnOrder"
                    inset
                    :label="
                      Setting.metadata.hideSpecialProductTabOnOrder
                        ? 'Hide'
                        : 'Show'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">New Product Tab</div>
                  <div>Hide New Product Tab on the remote ordering screen.</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.hideNewProductTabOnOrder"
                    inset
                    :label="
                      Setting.metadata.hideNewProductTabOnOrder
                        ? 'Hide'
                        : 'Show'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header> Modules </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Invoice Exit Guard</div>
                  <div>
                    Require warehouse guards to verify invoice totals before
                    merchandise exits and lock verified invoices from editing.
                  </div>
                  <div
                    v-if="!Setting.metadata.invoiceExitGuardEnabled"
                    class="caption primary--text mt-1"
                  >
                    Optional subscription: $25 per month
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.invoiceExitGuardEnabled"
                    color="primary"
                    inset
                    :label="Setting.metadata.invoiceExitGuardEnabled ? 'On' : 'Off'"
                    @change="confirmExitGuard"
                  />
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Collections</div>
                  <div>
                    Enable or disable collections feature, this will allow you
                    to create collections and assign categories to them.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.collections"
                    inset
                    :label="Setting.metadata.collections ? 'Enable' : 'Disable'"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Serialize</div>
                  <div>
                    Sale product with serial number, this feature allow you to
                    keep track items with serial numbers.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.serializeProduct"
                    inset
                    :label="
                      Setting.metadata.serializeProduct ? 'Enable' : 'Disable'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">Work Order</div>
                  <div>Enable work order feature.</div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.workOrder"
                    inset
                    :label="Setting.metadata.workOrder ? 'Show' : 'Hide'"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col class="py-0">
                  <div class="subtitle-2">History Tracking</div>
                  <div>
                    Records every change made to products, invoices, and
                    customers. When enabled, you can view detailed change logs
                    made by any user. This feature adds $50 per month to your
                    plan.
                  </div>
                </v-col>
                <v-col class="text-right shrink py-0">
                  <v-switch
                    v-model="Setting.metadata.historyTracking"
                    inset
                    :label="
                      Setting.metadata.historyTracking ? 'Enabled' : 'Disabled'
                    "
                  ></v-switch>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="section-block">
          <div>
            <div class="section-title">Logo</div>
            <div class="section-subtitle">Upload your business logo</div>
          </div>

          <div>
            <v-btn icon color="red">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
            <v-btn icon color="primary" @click="pickFile">
              <v-icon>mdi-paperclip</v-icon>
            </v-btn>
          </div>
        </div>

        <v-card-text>
          <v-layout align-center justify-center column fill-height>
            <v-layout
              class="ma-6"
              align-center
              justify-center
              column
              fill-height
            >
              <img height="120" v-if="imageUrl" :src="imageUrl" alt="Logo" />
              <img
                height="120"
                v-else
                src="../../assets/images/uploadphoto.png"
                alt="Logo"
              />
            </v-layout>
          </v-layout>

          <input
            type="file"
            ref="image"
            style="display: none"
            accept="image/*"
            @change="onFilePicked"
          />
        </v-card-text>

        <v-card-text>
          <v-row align="center">
            <v-col class="py-0" cols="8">
              <div class="subtitle-2">Time Zone</div>
              <div>
                Used for date filters, reports, and “today” calculations.
                Default is New York.
              </div>
            </v-col>

            <v-col class="text-right py-0" cols="4">
              <v-select
                v-model="Setting.metadata.timezone"
                :items="timezones"
                item-text="label"
                item-value="value"
                outlined
                dense
                hide-details
                label="Time Zone"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-text>
          <v-row>
            <v-col cols="12">
              <div class="section-title">Email Setting</div>
              <div class="section-subtitle">
                SMTP details for outgoing emails
              </div>
            </v-col>
            <v-col cols="12" sm="12">
              <v-text-field
                outlined
                hide-details
                dense
                v-model="Setting.smtpEmailAddress"
                label="Email Address"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-text-field
                outlined
                dense
                hide-details
                v-model="Setting.smtpHost"
                label="SMTP Setting"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="3">
              <v-text-field
                outlined
                dense
                hide-details
                v-model="Setting.smtpPort"
                label="SMTP Port"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="3">
              <v-text-field
                type="password"
                outlined
                hide-details
                dense
                v-model="Setting.smtpPassword"
                label="Password"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </div>
      <v-divider></v-divider>
      <v-card-actions class="setting-actions">
        <v-spacer />
        <v-btn rounded outlined @click="$router.go(-1)">Cancel</v-btn>
        <v-btn
          color="primary"
          rounded
          dark
          :loading="loading"
          @click="submit()"
        >
          Update Setting
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>
<script>
import RegisterStoreModule from "@/mixins/RegisterStoreModule";
import DateSelector from "@/components/DateSelector";
import DayPicker from "@/components/DayPicker";
import GoogleAddressInput from "@/modules/Google/GoogleAddressInput.vue";
import DateTimePicker from "@/components/DateTimePicker";
import userStore from "./store.js";
import settingService from "./service";
import { mapActions, mapGetters } from "vuex";
import ArrangeCategoryOrder from "./components/ArrangeCategoryOrder.vue";

const { validationMixin } = require("vuelidate");
const { required, minLength, email } = require("vuelidate/lib/validators");

export default {
  name: "setting",
  data() {
    return {
      newInvoiceDeletePhone: "",
      dialog: false,
      dialogDelete: false,
      timezone: "America/New_York",
      timezones: [
        {
          label: "New York (ET) [America/New_York]",
          value: "America/New_York",
        },
        { label: "Chicago (CT) [America/Chicago]", value: "America/Chicago" },
        { label: "Denver (MT) [America/Denver]", value: "America/Denver" },
        { label: "Phoenix (AZ) [America/Phoenix]", value: "America/Phoenix" },
        {
          label: "Los Angeles (PT) [America/Los_Angeles]",
          value: "America/Los_Angeles",
        },
        { label: "Anchorage [America/Anchorage]", value: "America/Anchorage" },
        { label: "Honolulu [Pacific/Honolulu]", value: "Pacific/Honolulu" },

        { label: "Toronto [America/Toronto]", value: "America/Toronto" },
        { label: "London [Europe/London]", value: "Europe/London" },
        { label: "Dubai [Asia/Dubai]", value: "Asia/Dubai" },
        { label: "Karachi [Asia/Karachi]", value: "Asia/Karachi" },
        { label: "Dhaka [Asia/Dhaka]", value: "Asia/Dhaka" },
        { label: "Delhi [Asia/Kolkata]", value: "Asia/Kolkata" },
      ],
      headers: [
        {
          text: "From ($)",
          value: "from",
        },
        { text: "To ($)", value: "to" },
        { text: "Mark Up (%)", value: "markup" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      editedIndex: -1,
      editedItem: {
        from: 0,
        to: 0,
        markup: 0,
      },
      defaultItem: {
        from: 0,
        to: 0,
        markup: 0,
      },
      type: "hex",
      hex: "#F0C316",
      loading: true,
      repairNestMarketplaceEnabled: false,
      repairNestMarketplaceLoading: true,
      repairNestMarketplaceSaving: false,
      repairNestMarketplaceError: "",
      repairNestMarketplaceApprovedBrands: [],
      repairNestMarketplaceApprovalStatus: "not_requested",
      imageName: "",
      imageUrl: "",
      imageFile: "",
      invoicePrintLabelItems: [
        { text: "Invoice", value: "invoice" },
        { text: "Consumer Invoice", value: "consumer invoice" },
        { text: "Receipt", value: "receipt" },
        { text: "Consignment Invoice", value: "consignment invoice" },
      ],
      Setting: null,
      metadata: {
        sendSMSOnInvoiceDelete: false,
        invoiceDueEmailReminders: true,
        invoiceDeleteAlertPhoneNumbers: [],
        remoteOrderStockCount: true,
        showOutOfStockItems: false,
        dynamicPrice: false,
        hidePaymentDate: false,
        openInvoiceListOnInvoice: false,
        workOrder: false,
        barcodeOnInvoice: false,
        outOfStock: false,
        lockOutOfStock: false,
        showAvgCostOnProduct: false,
        productDescription: false,
        serializeProduct: false,
        deletePrecaution: false,
        lockProductName: true,
        restrictDuplicateCustomerName: false,
        dynamicPriceList: [],
        dynamicSerializeLabel: "Serialize",
        dynamicNonSerializeLabel: "Non Serialize",
        invoicePreviewFormat: "html",
        packingSlipPreviewFormat: "html",
        showUnitPriceOnInvoice: true,
        showLineNumberOnInvoice: true,
        invoiceLimit: null,
        invoiceDue: null,
        invoiceViewLimit: null,
        forceCollection: false,
        collections: false,
        multiLineItemName: false,
        hideCustomerBalanceOnInvoice: false,
        hideImageOnOrder: false,
        historyTracking: false,
        hideSpecialProductTabOnOrder: false,
        hideNewProductTabOnOrder: false,
        invoicePrintLabel: "invoice",
        invoiceExitGuardEnabled: false,
        paymentQrCodeEnabled: false,
        paymentLink: "",
      },
    };
  },
  created() {
    this.registerStoreModule("setting", userStore);
    this.getSetting();
    this.loadRepairNestMarketplace();
    document.title = "smplsale - Setting";
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
  },
  // watch: {
  //   imageUrl(val) {
  //     this.Setting.image = val;
  //   },
  // },
  components: {
    DateSelector,
    GoogleAddressInput,
    ArrangeCategoryOrder,
    DayPicker,
    DateTimePicker,
  },
  computed: {
    ...mapGetters("global", ["currentBusiness"]),
    invoiceDeletePhoneNumbers() {
      if (
        !this.Setting ||
        !this.Setting.metadata ||
        !Array.isArray(this.Setting.metadata.invoiceDeleteAlertPhoneNumbers)
      ) {
        return [];
      }

      return this.Setting.metadata.invoiceDeleteAlertPhoneNumbers;
    },

    invoiceDeletePhoneDigits() {
      return this.normalizeUSPhoneNumber(this.newInvoiceDeletePhone);
    },

    canAddInvoiceDeletePhone() {
      if (this.invoiceDeletePhoneDigits.length !== 10) {
        return false;
      }

      return !this.invoiceDeletePhoneNumbers.includes(
        this.invoiceDeletePhoneDigits
      );
    },

    invoiceDeletePhoneError() {
      if (!this.newInvoiceDeletePhone) {
        return [];
      }

      const rawDigits = String(this.newInvoiceDeletePhone).replace(/\D/g, "");
      const normalizedPhone = this.normalizeUSPhoneNumber(
        this.newInvoiceDeletePhone
      );

      if (rawDigits.length > 0 && normalizedPhone.length !== 10) {
        return ["Enter a valid 10-digit U.S. phone number."];
      }

      if (this.invoiceDeletePhoneNumbers.includes(normalizedPhone)) {
        return ["This phone number has already been added."];
      }

      return [];
    },
    color: {
      get() {
        return this[this.type];
      },
      set(v) {
        this[this.type] = v;
      },
    },
    showColor() {
      if (typeof this.color === "string") return this.color;

      return JSON.stringify(
        Object.keys(this.color).reduce((color, key) => {
          color[key] = Number(this.color[key].toFixed(2));
          return color;
        }, {}),
        null,
        2
      );
    },
    // showColor() {
    //   if (typeof this.color === "string") return this.color;

    //   return JSON.stringify(
    //     Object.keys(this.color).reduce((color, key) => {
    //       color[key] = Number(this.color[key].toFixed(2));
    //       return color;
    //     }, {}),
    //     null,
    //     2
    //   );
    // },
    formTitle() {
      return this.editedIndex === -1 ? "New Item" : "Edit Item";
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.Setting.email.$dirty) return errors;
      if (!this.$v.Setting.email) errors.push("Email is not valid");
      if (!this.$v.Setting.email.required) errors.push("Email is required.");
      return errors;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.Setting.name.$dirty) return errors;
      if (!this.$v.Setting.name.minLength)
        errors.push("Field must be at least 5 characters long");
      if (!this.$v.Setting.name.required)
        errors.push("Business Name is required.");
      return errors;
    },
    phoneNumberErrors() {
      const errors = [];
      if (!this.$v.Setting.phoneNumber.$dirty) return errors;
      if (!this.$v.Setting.phoneNumber.required)
        errors.push("Phone Number is required.");
      return errors;
    },
    marketplaceStatusLabel() {
      return this.repairNestMarketplaceApprovalStatus === "approved"
        ? "Approved"
        : "Pending approval";
    },
    marketplaceStatusColor() {
      return this.repairNestMarketplaceApprovalStatus === "approved"
        ? "success"
        : "info";
    },
    approvedBrandNames() {
      const names = {
        repairnest: "RepairNest",
        autorepair: "Auto Repair Desk",
        storesync: "StoreSync",
      };
      return this.repairNestMarketplaceApprovedBrands
        .map(brand => names[brand] || brand)
        .join(", ");
    },
  },
  mixins: [validationMixin, RegisterStoreModule],
  validations: {
    Setting: {
      name: {
        required,
        minLength: minLength(5),
      },
      email: {
        required,
        email,
      },
      phoneNumber: {
        required,
      },
    },
  },
  methods: {
    applyMarketplaceResult(result) {
      this.repairNestMarketplaceEnabled = !!result.enabled;
      this.repairNestMarketplaceApprovedBrands = Array.isArray(result.approvedBrands)
        ? result.approvedBrands
        : [];
      this.repairNestMarketplaceApprovalStatus = result.approvalStatus ||
        (this.repairNestMarketplaceApprovedBrands.length ? "approved" : "pending");
    },
    marketplaceErrorMessage(error) {
      return (
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "Unable to update RepairNest Marketplace. Please try again."
      );
    },
    async loadRepairNestMarketplace() {
      this.repairNestMarketplaceLoading = true;
      this.repairNestMarketplaceError = "";
      try {
        const result = await settingService.getRepairNestMarketplace();
        this.applyMarketplaceResult(result);
      } catch (error) {
        this.repairNestMarketplaceError = this.marketplaceErrorMessage(error);
      } finally {
        this.repairNestMarketplaceLoading = false;
      }
    },
    async updateRepairNestMarketplace(enabled) {
      const previousValue = !enabled;
      this.repairNestMarketplaceSaving = true;
      this.repairNestMarketplaceError = "";
      try {
        const result = await settingService.updateRepairNestMarketplace(
          enabled
        );
        this.applyMarketplaceResult(result);
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: `Market participation ${
              result.enabled ? "requested" : "withdrawn"
            }.`,
          });
      } catch (error) {
        this.repairNestMarketplaceEnabled = previousValue;
        this.repairNestMarketplaceError = this.marketplaceErrorMessage(error);
      } finally {
        this.repairNestMarketplaceSaving = false;
      }
    },
    async confirmExitGuard(value) {
      if (value) {
        const result = await this.$swal({
          title: "Enable Invoice Exit Guard?",
          text: "This optional feature costs $25 per month. The charge begins when you save Settings.",
          icon: "info",
          showCancelButton: true,
          confirmButtonText: "Enable for $25/month",
        });
        if (!result.value) {
          this.$set(this.Setting.metadata, "invoiceExitGuardEnabled", false);
        }
        return;
      }

      const result = await this.$swal({
        title: "Turn off Invoice Exit Guard?",
        html: "<strong>Turning off this module does not stop the $25 monthly charge.</strong><br><br>This setting only blocks access to the feature. Please contact smplsale administration separately to cancel the subscription charge, because turning off the module does not notify us that you want to cancel.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Turn Off Feature Only",
        cancelButtonText: "Keep It On",
      });
      if (!result.value) {
        this.$set(this.Setting.metadata, "invoiceExitGuardEnabled", true);
      }
    },
    ...mapActions("global", ["initBusiness"]),
    normalizeUSPhoneNumber(value) {
      let digits = String(value || "").replace(/\D/g, "");

      if (digits.length === 11 && digits.startsWith("1")) {
        digits = digits.substring(1);
      }

      return digits.substring(0, 10);
    },

    formatPhoneNumber(value) {
      const digits = this.normalizeUSPhoneNumber(value);

      if (digits.length !== 10) {
        return value || "";
      }

      return `(${digits.substring(0, 3)}) ${digits.substring(
        3,
        6
      )}-${digits.substring(6)}`;
    },

    formatInvoiceDeletePhoneInput(value) {
      let digits = String(value || "").replace(/\D/g, "");

      if (digits.length === 11 && digits.startsWith("1")) {
        digits = digits.substring(1);
      }

      digits = digits.substring(0, 10);

      if (digits.length <= 3) {
        this.newInvoiceDeletePhone = digits;
        return;
      }

      if (digits.length <= 6) {
        this.newInvoiceDeletePhone = `(${digits.substring(
          0,
          3
        )}) ${digits.substring(3)}`;
        return;
      }

      this.newInvoiceDeletePhone = `(${digits.substring(
        0,
        3
      )}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    },

    addInvoiceDeletePhone() {
      const phone = this.normalizeUSPhoneNumber(this.newInvoiceDeletePhone);

      if (phone.length !== 10) {
        return;
      }

      if (this.invoiceDeletePhoneNumbers.includes(phone)) {
        return;
      }

      this.Setting.metadata.invoiceDeleteAlertPhoneNumbers.push(phone);
      this.newInvoiceDeletePhone = "";
    },

    removeInvoiceDeletePhone(index) {
      this.Setting.metadata.invoiceDeleteAlertPhoneNumbers.splice(index, 1);
    },
    setAddress(addressData) {
      this.Setting.address = addressData.address;
      this.Setting.city = addressData.city;
      this.Setting.state = addressData.state;
      this.Setting.zip = addressData.zip;
    },
    changeDynamicPrice(val) {
      console.log("changeDynamicPrice", val);
      if (val) {
        if (
          this.Setting.metadata &&
          this.Setting.metadata &&
          !this.Setting.metadata.dynamicPriceList
        ) {
          this.Setting.metadata.dynamicPriceList = [];
          console.log("this.Setting.metadata.dynamicPriceList");
          this.initialize();
        }
      }
    },
    initialize() {
      this.metadata.dynamicPriceList = [
        { from: 1, to: 5, markup: 75 },
        { from: 6, to: 10, markup: 50 },
        { from: 11, to: 15, markup: 40 },
        { from: 16, to: 20, markup: 30 },
        { from: 21, to: 99, markup: 25 },
        { from: 100, to: 199, markup: 23 },
        { from: 200, to: 300, markup: 20 },
        { from: 301, to: 399, markup: 15 },
        { from: 400, to: 500, markup: 10 },
        { from: 501, to: 599, markup: 8 },
        { from: 600, to: 700, markup: 5 },
      ];
    },
    editItem(item) {
      this.editedIndex = this.Setting.metadata.dynamicPriceList.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    setDue(invoiceDue) {
      this.Setting.metadata.invoiceDue = invoiceDue;
    },

    deleteItem(item) {
      this.editedIndex = this.Setting.metadata.dynamicPriceList.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      this.Setting.metadata.dynamicPriceList.splice(this.editedIndex, 1);
      this.closeDelete();
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    save() {
      if (this.editedIndex > -1) {
        Object.assign(
          this.Setting.metadata.dynamicPriceList[this.editedIndex],
          this.editedItem
        );
      } else {
        this.Setting.metadata.dynamicPriceList.push(this.editedItem);
      }
      this.close();
    },
    pickFile() {
      this.$refs.image.click();
    },
    onFilePicked(e) {
      const files = e.target.files;
      if (files[0] !== undefined) {
        this.imageName = files[0].name;
        if (this.imageName.lastIndexOf(".") <= 0) {
          return;
        }
        const fr = new FileReader();
        fr.readAsDataURL(files[0]);
        fr.addEventListener("load", () => {
          this.imageUrl = fr.result;
          this.Setting.image = this.imageUrl;
          this.imageFile = files[0]; // this is an image file that can be sent to server...
        });
      } else {
        this.imageName = "";
        this.imageFile = "";
        this.imageUrl = "";
      }
    },
    submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      const metadata = this.Setting.metadata || {};

      if (metadata.paymentQrCodeEnabled) {
        let paymentUrl;
        try {
          paymentUrl = new URL(String(metadata.paymentLink || "").trim());
        } catch (error) {
          paymentUrl = null;
        }

        if (!paymentUrl || !["http:", "https:"].includes(paymentUrl.protocol)) {
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: "Enter a valid HTTP or HTTPS payment link.",
            });
          return;
        }

        this.$set(this.Setting.metadata, "paymentLink", paymentUrl.toString());
      }

      const phoneNumbers = Array.isArray(
        metadata.invoiceDeleteAlertPhoneNumbers
      )
        ? metadata.invoiceDeleteAlertPhoneNumbers
        : [];

      const cleanedPhoneNumbers = [
        ...new Set(
          phoneNumbers
            .map((phone) => this.normalizeUSPhoneNumber(phone))
            .filter((phone) => phone.length === 10)
        ),
      ];

      if (metadata.sendSMSOnInvoiceDelete && cleanedPhoneNumbers.length === 0) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Add at least one valid 10-digit U.S. phone number.",
          });

        return;
      }

      this.$set(
        this.Setting.metadata,
        "invoiceDeleteAlertPhoneNumbers",
        cleanedPhoneNumbers
      );

      this.$set(
        this.Setting.metadata,
        "remoteOrderBackgroundColor",
        this.showColor
      );

      this.loading = true;

      return settingService
        .update(this.Setting.id, this.Setting)
        .then((result) => {
          if (!result.status) {
            throw new Error(result.message || "Unable to update the setting.");
          }

          this.initBusiness(this.Setting);

          return this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "success",
              title: "Setting is updated!",
            });
        })
        .then(() => {
          this.$router.go(-1);
        })
        .catch((err) => {
          const message =
            err?.data?.message ||
            err?.response?.data?.message ||
            err?.message ||
            "Unable to update the setting.";

          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: message,
            });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getSetting() {
      this.loading = true;
      return settingService.getById(this.currentBusiness.id).then((result) => {
        // // console.log("got setting", result, this.metadata);
        this.Setting = result.data;
        const savedMetadata = this.Setting.metadata || {};
        this.Setting.metadata = {
          ...this.metadata,
          ...this.Setting.metadata,
        };

        if (
          !["html", "pdf"].includes(
            this.Setting.metadata.invoicePreviewFormat
          )
        ) {
          this.$set(this.Setting.metadata, "invoicePreviewFormat", "html");
        }
        if (!Object.prototype.hasOwnProperty.call(savedMetadata, "packingSlipPreviewFormat")) {
          this.$set(
            this.Setting.metadata,
            "packingSlipPreviewFormat",
            this.Setting.metadata.invoicePreviewFormat
          );
        } else if (!['html', 'pdf'].includes(this.Setting.metadata.packingSlipPreviewFormat)) {
          this.$set(this.Setting.metadata, "packingSlipPreviewFormat", "html");
        }

        if (this.Setting.imageUrl) this.imageUrl = this.Setting.imageUrl;

        if (!this.Setting.metadata.timezone) {
          this.$set(this.Setting.metadata, "timezone", "America/New_York");
        }

        if (this.Setting.metadata?.remoteOrderBackgroundColor) {
          try {
            this.color = JSON.parse(
              this.Setting.metadata.remoteOrderBackgroundColor
            );
          } catch (e) {
            this.color = this.Setting.metadata.remoteOrderBackgroundColor;
          }
        }

        // // console.log("got setting", this.Setting);
        this.loading = false;
      });
    },
  },
};
</script>

<style scoped>
.setting-page {
  background: #f8fafc;
  min-height: 100vh;
}

.setting-card {
  border-radius: 20px;
  background: #ffffff;
  overflow: hidden;
}

.marketplace-section {
  background: transparent;
}

.marketplace-heading {
  background: transparent;
  color: inherit;
}

.marketplace-heading .section-title {
  color: inherit;
}

.marketplace-heading .section-subtitle {
  color: inherit;
  opacity: 0.68;
}

.marketplace-card {
  border-radius: 16px;
}

.marketplace-card__content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.marketplace-card__icon {
  align-items: center;
  border-radius: 12px;
  display: flex;
  flex: 0 0 48px;
  height: 48px;
  justify-content: center;
}

.marketplace-card__copy {
  flex: 1;
  min-width: 0;
}

.marketplace-card__heading {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.marketplace-card__description {
  opacity: 0.78;
  font-size: 13px;
  line-height: 1.5;
}

@media (max-width: 600px) {
  .marketplace-card__content {
    display: block;
  }

  .marketplace-card__icon {
    margin-bottom: 12px;
  }

  .marketplace-card__heading {
    align-items: flex-start;
  }
}

.setting-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.setting-title-wrap {
  display: flex;
  align-items: center;
}

.page-title {
  font-size: 22px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1.15;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 3px;
}

.section-block {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.section-title {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
}

.section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.setting-panels {
  border-radius: 0;
}

.setting-panels .v-expansion-panel {
  border-bottom: 1px solid #e2e8f0;
}

.setting-panels .v-expansion-panel-header {
  font-weight: 900;
  color: #0f172a;
}

.setting-panels .v-expansion-panel-content {
  background: #ffffff;
}

.setting-panels .v-row {
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}

.setting-panels .v-row:last-child {
  border-bottom: 0;
}

.subtitle-2 {
  font-weight: 900;
  color: #0f172a;
}

.subtitle-2 + div,
.v-col > div:not(.subtitle-2):not(.section-title):not(.section-subtitle) {
  color: #64748b;
  font-size: 13px;
}

.setting-actions {
  position: sticky;
  bottom: 0;
  z-index: 5;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  padding: 14px 16px;
}

@media (max-width: 600px) {
  .setting-page {
    padding: 8px !important;
  }

  .setting-header {
    align-items: flex-start;
  }

  .page-title {
    font-size: 20px;
  }

  .section-block {
    align-items: flex-start;
    flex-direction: column;
  }

  .setting-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .setting-actions .v-btn {
    width: 100%;
  }
}
</style>
